/**
 *
 * Tooltip/Tooltip
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CloseIcon from 'components/HomeReferral/images/close.svg';

const useStyles = makeStyles(theme => ({
  wrapper: {
    marginTop: 16,
    padding: '0 5%',
    maxWidth: '100%',
    width: '100%',
    '& > h4': {
      overflow: 'hidden',
      [theme.breakpoints.up('600')]: {
        '&::after': {
          content: `""`,
          height: '0.6em',
          verticalAlign: 'bottom',
          width: '100%',
          marginRight: '-100%',
          marginLeft: 32,
          borderTop: '1px solid #EFEFEF',
          display: 'inline-block',
        },
        '&::before': {
          content: `""`,
          height: '0.6em',
          verticalAlign: 'bottom',
          width: '100%',
          marginLeft: '-100%',
          marginRight: 32,
          borderTop: '1px solid #EFEFEF',
          display: 'inline-block',
        },
      },
    },
  },
  container: {
    marginTop: 16,
    borderRadius: 10,
    border: '1px solid #E3E3E3',
    padding: 16,
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: '#E3E3E3',
    marginTop: 8,
    marginBottom: 8,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 68,
  },
  icon: {
    height: 28,
    width: 28,
    objectFit: 'contain',
    marginRight: 32,
  },
  label: {
    flex: 1,
  },
  button: {
    fontFamily: 'MadaSemiBold',
    fontSize: '0.9375rem',
    width: 195,
    minHeight: 38,
    marginLeft: 32,
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    borderRadius: 3,
  },
  accessCodeContainer: {
    width: 220,
    height: '100%',
  },
  accessCodeWrapper: {
    backgroundColor: '#F8F9F9',
    borderRadius: 9,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 12px',
  },
  accessCodeLabel: {
    fontFamily: 'MadaBold',
    fontSize: '2.313rem',
    lineHeight: '1.1',
  },
  qrCodeWrapper: {
    width: 116,
    height: 116,
    border: '1px solid #979797',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFFF',
  },
  accessCodeButton: {
    ...theme.typography.pxSmall,
    backgroundColor: '#4B4B4B',
    color: '#FFFFFF',
    borderRadius: 23,
    minHeight: 35,
    width: '100%',
    marginTop: 20,
  },
  closeIconWrapper: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  closeIcon: {
    height: 14,
    width: 14,
  },
  header: {
    paddingRight: 16,
  },
}));

const useTooltipStyles = makeStyles(theme =>
  createStyles({
    tooltip: {
      backgroundColor: '#FFFFFF',
      color: theme.palette.text.primary,
      border: `1px solid #d7d7d7`,
      width: 500,
      borderRadius: 10,
      opacity: '1 !important',
      padding: '20px 18px',
      [theme.breakpoints.down('xs')]: {
        marginTop: 16,
      },
      maxWidth: '370px',
    },
    popper: {
      zIndex: 1301,
    },
    arrow: {
      '&::before': {
        border: `1px solid #d7d7d7`,
        backgroundColor: '#FFFFFF',
        boxSizing: 'border-box',
      },
    },
  }),
);

function CustomTooltip({ body, children, header, hover, trackEvent }) {
  const classes = useStyles();
  const tooltipClasses = useTooltipStyles();
  const [open, setOpen] = useState(false);

  const handleOpen = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && trackEvent)
      trackEvent('Assessment Referral - Referral Panel Information Clicked');
    setOpen(true);
  };
  const handleClose = e => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <Tooltip
      classes={tooltipClasses}
      placement="top"
      onClose={handleClose}
      open={open}
      arrow
      disableFocusListener
      disableHoverListener={!hover}
      disableTouchListener
      onOpen={handleOpen}
      interactive
      TransitionComponent={({ children: transitionChildren }) =>
        transitionChildren
      }
      title={
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <ButtonBase
              onClick={handleClose}
              disableRipple
              className={classes.closeIconWrapper}
            >
              <img alt="close" src={CloseIcon} className={classes.closeIcon} />
            </ButtonBase>
            {!_isEmpty(header) && (
              <Typography
                variant="h6"
                color="textPrimary"
                gutterBottom
                className={classes.header}
              >
                {header}
              </Typography>
            )}
            <Typography variant="overline" color="textPrimary">
              {!_isEmpty(body) && body}
            </Typography>
          </Box>
        </ClickAwayListener>
      }
      PopperProps={{
        modifiers: { flip: { enabled: false } },
      }}
    >
      {React.cloneElement(children, { onClick: handleOpen })}
    </Tooltip>
  );
}

CustomTooltip.propTypes = {
  body: PropTypes.string,
  children: PropTypes.node,
  header: PropTypes.string,
  hover: PropTypes.bool,
  trackEvent: PropTypes.func,
};

export default CustomTooltip;
